import { IconMinus, IconPlus, IconUser, IconUsersGroup } from '@tabler/icons-react';
import { RIDES_SEX } from '@actschurch/ccms-sdk';
import type { PartyMode } from '@/store/ridesWizard';
import { useCopy } from '@/lib/settings';
import { PickerCard, PickerGroup } from '@/components/ui/PickerCard';
import { Callout } from '@/components/ui/Callout';
import '@/styles/flows.css';

interface Props {
  party: PartyMode | null;
  soloSexId: number | null;
  maleCount: number;
  femaleCount: number;
  onChange: (patch: { party?: PartyMode; soloSexId?: number | null; maleCount?: number; femaleCount?: number }) => void;
  /** Cap on the whole group (settings.maxPartySize). */
  maxSeats?: number;
}

/** A 48 px minus / count / plus control with a label on the left (right-aligned, so rows line up). */
export function CountStepper({ label, hint, value, onChange, min = 0, max = 6 }: { label: string; hint?: string; value: number; onChange: (n: number) => void; min?: number; max?: number }) {
  return (
    <div className="rides-srow" style={{ alignItems: 'center' }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700, fontSize: 17 }}>{label}</span>
        {hint && <span style={{ display: 'block', color: 'var(--rides-muted)', fontSize: 15 }}>{hint}</span>}
      </span>
      <span className="rides-stepper" role="group" aria-label={label}>
        <button type="button" aria-label={`One fewer: ${label.toLowerCase()}`} disabled={value <= min} onClick={() => onChange(value - 1)}><IconMinus size={20} aria-hidden="true" /></button>
        <output aria-live="polite">{value}</output>
        <button type="button" aria-label={`One more: ${label.toLowerCase()}`} disabled={value >= max} onClick={() => onChange(value + 1)}><IconPlus size={20} aria-hidden="true" /></button>
      </span>
    </div>
  );
}

/**
 * How many men and women are travelling together, with the plain rule that goes with it.
 * `child` switches the labels to include boys and girls.
 */
export function PartyCounter({ maleCount, femaleCount, onChange, maxSeats, child }: { maleCount: number; femaleCount: number; onChange: (patch: { maleCount?: number; femaleCount?: number }) => void; maxSeats?: number; child?: boolean }) {
  const { t, settings } = useCopy();
  const cap = Math.max(2, maxSeats ?? settings.maxPartySize ?? 6);
  const total = maleCount + femaleCount;
  const femaleRule = settings.femaleDriverRuleEnabled;
  return (
    <div className="rides-flow-stack">
      <div className="rides-card" style={{ padding: '4px 16px' }}>
        <CountStepper label={child ? 'Boys and men' : 'Men'} value={maleCount} onChange={(n) => onChange({ maleCount: n })} max={Math.max(0, cap - femaleCount)} />
        <CountStepper label={child ? 'Girls and women' : 'Women'} value={femaleCount} onChange={(n) => onChange({ femaleCount: n })} max={Math.max(0, cap - maleCount)} />
      </div>
      <p className="rides-help" aria-live="polite">
        {total === 1 ? '1 person' : `${total} people`}, so you need {total === 1 ? '1 seat' : `${total} seats`}. Up to {cap} people can travel together.
      </p>
      {femaleRule && total >= 2 && (
        <Callout tone="info">
          {femaleCount === 0
            ? "A female driver can only take a group that includes at least one woman, so you'll see male drivers only."
            : t('party.group_note')}
        </Callout>
      )}
    </div>
  );
}

/**
 * "Just me" (with sex) or "A group travelling together" (men and women counts). Kept for older
 * screens; the rider flow now asks these one screen at a time with PickerCard and PartyCounter.
 */
export function PartyCompositionPicker({ party, soloSexId, maleCount, femaleCount, onChange, maxSeats }: Props) {
  const setSolo = (sexId: number) =>
    onChange({ party: 'solo', soloSexId: sexId, maleCount: sexId === RIDES_SEX.Male ? 1 : 0, femaleCount: sexId === RIDES_SEX.Female ? 1 : 0 });
  return (
    <div className="rides-flow-stack">
      <PickerGroup ariaLabel="Who's travelling">
        <PickerCard
          selected={party === 'solo'}
          onSelect={() => onChange({ party: 'solo', maleCount: soloSexId === RIDES_SEX.Male ? 1 : 0, femaleCount: soloSexId === RIDES_SEX.Female ? 1 : 0 })}
          media={<IconUser size={24} aria-hidden="true" />}
          title="Just me"
          description="I'm travelling on my own"
        />
        <PickerCard
          selected={party === 'group'}
          onSelect={() => onChange({ party: 'group', soloSexId: null, maleCount: Math.max(maleCount, party === 'group' ? 0 : 1), femaleCount: Math.max(femaleCount, party === 'group' ? 0 : 1) })}
          media={<IconUsersGroup size={24} aria-hidden="true" />}
          title="Me and others"
          description="Family or friends travelling with me"
        />
      </PickerGroup>
      {party === 'solo' && (
        <PickerGroup label="Are you male or female?">
          <PickerCard selected={soloSexId === RIDES_SEX.Female} onSelect={() => setSolo(RIDES_SEX.Female)} title="Female" />
          <PickerCard selected={soloSexId === RIDES_SEX.Male} onSelect={() => setSolo(RIDES_SEX.Male)} title="Male" />
        </PickerGroup>
      )}
      {party === 'group' && <PartyCounter maleCount={maleCount} femaleCount={femaleCount} onChange={onChange} maxSeats={maxSeats} />}
    </div>
  );
}
